import React from 'react';
import { CheckCircle, Circle } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import { scaleMotionProps2 } from 'constants/animationProps';
import fundmoreai from 'assets/fundmoreai.png';
import { useTranslation } from 'react-i18next';
import styles from '../../../styles/features/onboarding-layout.module.css';
/**
 * Page4 function handles the rendering and functionality of the fourth page of the application.
 *
 * @param {Object} props - The props object containing the necessary data and functions.
 * @param {string} props.user - The name of the user.
 * @param {function} props.setLoanPurpose - The function to set the loan purpose.
 * @param {string} props.loanPurpose - The current loan purpose.
 * @param {function} props.setPage - The function to set the current page.
 * @param {boolean} props.isLoading - A flag indicating if the page is currently loading.
 * @param {function} props.setDirection - The function to set the direction of the page transition.
 * @param {boolean} props.direction - The current direction of the page transition.
 * @returns {JSX.Element} - The JSX element representing the fourth page of the application.
 */
const Page4 = ({
  user,
  setLoanPurpose,
  loanPurpose,
  setPage,
  isLoading,
  setDirection,
  direction,
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  return (
    <div className={styles.contentWrapper}>
      <AnimatePresence mode="wait">
        <motion.div
          {...scaleMotionProps2}
          className={`${styles.formContainer} ${styles.fadeInContent}`}
        >
          <div className={styles.logoContainer}>
            <img src={fundmoreai} alt="Fundmore" className={styles.logoIcon} />
            <div className={styles.logoText}>
              <span className={styles.logoTextLight}>fund</span>
              <span className={styles.logoTextBold}>more</span>
              <span className={styles.logoTextGreen}>.ai</span>
            </div>
          </div>
          <span className={styles.srOnly}>
            {t('onboarding:greeting', { name: user })}
          </span>

          <div className={styles.progressContainer}>
            <div className={styles.progressBar} />
          </div>

          <h2 className={styles.heading}>
            {t('onboarding:whatAreLookingFor')}
          </h2>
          <p className={styles.subheading}>
            {t('onboarding:goalSubtitle')}
          </p>

          <div className={styles.optionsContainer}>
            <div
              onClick={() => setLoanPurpose('purchase')}
              className={`${styles.optionCard} ${
                loanPurpose === 'purchase'
                  ? styles.optionCardSelected
                  : styles.optionCardDefault
              }`}
            >
              <div className={styles.optionContent}>
                <span className={styles.optionLabel}>
                  {t('onboarding:loanPurpose.purchase.label')}
                </span>
                <span className={styles.optionSubtext}>
                  {t('onboarding:loanPurpose.purchase.description')}
                </span>
              </div>
              <div className={styles.optionIcon}>
                {loanPurpose === 'purchase' ? (
                  <CheckCircle size={24} className={styles.optionIconCheck} />
                ) : (
                  <Circle size={24} className={styles.choiceIconUnselected} />
                )}
              </div>
            </div>
            <div
              onClick={() => setLoanPurpose('refinance')}
              className={`${styles.optionCard} ${
                loanPurpose === 'refinance'
                  ? styles.optionCardSelected
                  : styles.optionCardDefault
              }`}
            >
              <div className={styles.optionContent}>
                <span className={styles.optionLabel}>
                  {t('onboarding:loanPurpose.refinance.label')}
                </span>
                <span className={styles.optionSubtext}>
                  {t('onboarding:loanPurpose.refinance.description')}
                </span>
              </div>
              <div className={styles.optionIcon}>
                {loanPurpose === 'refinance' ? (
                  <CheckCircle size={24} className={styles.optionIconCheck} />
                ) : (
                  <Circle size={24} className={styles.choiceIconUnselected} />
                )}
              </div>
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={() => {
                  setPage(3);
                  setDirection(false);
                }}
                className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonFlexOne}`}
              >
                {t('common:buttons.back')}
              </button>
              <button
                type="button"
                disabled={loanPurpose === ''}
                onClick={() => {
                  setPage(5);
                  setDirection(true);
                }}
                className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonFlexTwo}`}
              >
                {isLoading && direction
                  ? t('common:buttons.pleaseWait')
                  : t('common:buttons.continue')}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Page4;
